import { useEffect, useState, useCallback, useRef } from "react";
import apiClient from "utils/apiClient";
import { PM_SERVICE_BASE_URL } from "constants";

const PAGE_SIZE = 100;

export const useKanbanTasks = (projectId, iterationId) => {
  const [tasks, setTasks] = useState([]);
  const [pageByStatus, setPageByStatus] = useState({});
  const [hasMoreByStatus, setHasMoreByStatus] = useState({});
  const [loadingByStatus, setLoadingByStatus] = useState({});

  const pageRef = useRef({});
  const loadingRef = useRef({});

  const fetchTasksForColumn = useCallback(
    async (status) => {
      if (loadingRef.current[status]) return;
      const page = pageRef.current[status] || 0;

      try {
        loadingRef.current[status] = true;
        setLoadingByStatus((prev) => ({ ...prev, [status]: true }));

        const res = await apiClient.get(
          `${PM_SERVICE_BASE_URL}/api/v1/projects/${projectId}/tasks`,
          {
            params: {
              status,
              iterationId,
              page,
              size: PAGE_SIZE,
              sort: "position,asc",
            },
          },
        );

        const newTasks = res.data.content;

        setTasks((prev) => {
          const existingIds = new Set(prev.map((t) => t.id));
          const filtered = newTasks.filter((t) => !existingIds.has(t.id));
          return [...prev, ...filtered];
        });

        pageRef.current[status] = page + 1;

        setHasMoreByStatus((prev) => ({
          ...prev,
          [status]: !res.data.last,
        }));
      } catch (err) {
        console.error("Failed fetching tasks:", err);
      } finally {
        loadingRef.current[status] = false;
        setLoadingByStatus((prev) => ({ ...prev, [status]: false }));
      }
    },
    [projectId, iterationId],
  );

  const reset = () => {
    setTasks([]);
    setPageByStatus({});
    setHasMoreByStatus({});
    setLoadingByStatus({});
    pageRef.current = {};
    loadingRef.current = {};
  };

  useEffect(() => {
    if (!projectId) return;
    reset();
  }, [projectId, iterationId]);

  return {
    tasks,
    setTasks,
    fetchTasksForColumn,
    hasMoreByStatus,
    loadingByStatus,
  };
};
